import React, { useState, useEffect } from "react";
import { UseUserAuth } from "./context/UseAuthContext";
import PageNav from "./components/PageNav";
import { useNavigate } from "react-router-dom";
import { generateSlug } from './utils';

export default function PrintBookOrder() {
  const { user } = UseUserAuth();
  const [printOrders, setPrintOrders] = useState([]);
  const [thumbNailImages, setThumbNailImages] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    // Fetch print orders data when the component mounts
    async function fetchPrintOrders() {
      try {
        const idToken = await user.getIdToken();
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND}/user/printOrders`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: idToken,
            },
            body: JSON.stringify(),
          }
        );
        
        if (!response.ok) {
          console.log("Something went wrong fetching print orders");
          return;
        }
        
        const result = await response.json();
        setPrintOrders(result);
        console.log("Print orders data:", result);

        // Fetch thumbnail images for each print order
        try {
          const topImages = [];
          for (const item of result) {
            const storyId = item[0]; // storyID
            
            const response2 = await fetch(
              `${process.env.REACT_APP_BACKEND}/user/imageOutput`,
              {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  Authorization: idToken,
                },
                body: JSON.stringify({
                  bookId: `${storyId}_${1}`, // First image: userID-storyID_1
                }),
              }
            );
            
            if (response2.ok) {
              const blob = await response2.blob();
              const reader = new FileReader();
              reader.readAsDataURL(blob);
              let url;
              await new Promise((resolve) => {
                reader.addEventListener(
                  "load",
                  () => {
                    url = reader.result;
                    resolve();
                  },
                  false
                );
              });
              topImages.push(url);
            }
          }
          setThumbNailImages(topImages);
        } catch (error) {
          console.error("Error fetching thumbnail images:", error);
        }
      } catch (error) {
        console.error("Error fetching print orders:", error);
      }
    }

    if (user) {
      fetchPrintOrders();
    }
  }, [user]);

  // Function to handle Continue button click
  async function handleContinueToPrint(e, storyId, storyTitle, orderId) {
    e.preventDefault();
    
    try {
      // Generate slug from story title
      const slug = generateSlug(storyTitle);
      
      // Navigate to print-books/slug page
             
      navigate(`/print-book-order/${slug}`, { 
        state: { 
          storyId: storyId,
          storyTitle: storyTitle,
          orderId:orderId
        } 
      });
    } catch (error) {
      console.error("Error navigating to print page:", error);
    }
  }

  // Function to format date from storyId (timestamp)
  function formatDate(storyId) {
    const date = new Date(parseInt(storyId));
    const year = date.getFullYear();
    const day = date.getDate();
    
    const monthNames = [
      "January", "February", "March", "April", "May", "June",
      "July", "August", "September", "October", "November", "December"
    ];
    const month = monthNames[date.getMonth()];
    
    return `${month} ${day}, ${year}`;
  }

  // Component to display print orders
  function PrintOrdersDisplay() {
    // Combine each item in the printOrders array with the corresponding image URL
    const combinedOrders = printOrders.map((item, index) => {
      const storyId = item[0];      // storyID
      const storyTitle = item[1];   // storyTitle
      const orderId = item[3];      // orderId
      const url = thumbNailImages[index]; // corresponding thumbnail image
      const createdAt = formatDate(storyId);
      
      return { storyId, storyTitle, orderId, url, createdAt };
    });

    return (
      <div className="bg-white">
        <div className="mx-auto max-w-3xl px-4 py-16 sm:px-6 sm:py-24">
          <div className="max-w-xl">
            <h1
              id="print-orders-heading"
              className="text-3xl font-bold tracking-tight text-gray-900"
            >
              Your Print Books
            </h1>
            <p className="mt-2 text-sm text-gray-500">
            Track the status of your book orders            </p>
          </div>

          <div className="mt-12 space-y-16 sm:mt-16">
            {combinedOrders.map((item, index) => (
              <section
                key={item.storyId}
                aria-labelledby={`${item.storyId}-heading`}
              >
                <div className="space-y-1 md:flex md:items-baseline md:space-x-4 md:space-y-0"></div>
                <div className="-mb-6 mt-6 flow-root divide-y divide-gray-200 border-t border-gray-200">
                  <div key={index} className="py-6 sm:flex">
                    <div className="flex space-x-4 sm:min-w-0 sm:flex-1 sm:space-x-6 lg:space-x-8">
                      <img
                        src={item.url}
                        className="h-20 w-20 flex-none rounded-md object-cover object-center sm:h-48 sm:w-48"
                        alt={item.storyTitle}
                      />
                      <div className="min-w-0 flex-1 pt-1.5 sm:pt-0">
                        <p className="mt-1 font-medium text-gray-900">
                          {item.storyTitle}
                        </p>
                        <span
                          className="mt-1 text-sm text-gray-700"
                          aria-hidden="true"
                        >
                          Created On: {item.createdAt}
                        </span>
                        <h3 className="mt-1 text-sm font-medium text-gray-800">
                          Order# {item.orderId}
                        </h3>
                        <p className="mt-1 text-sm text-gray-600">
                          Story ID: {item.storyId}
                        </p>
                      </div>
                    </div>
                    <div className="mt-6 space-y-4 sm:ml-6 sm:mt-0 sm:w-40 sm:flex-none">
                      <button
                        type="button"
                        onClick={(e) => {
                          handleContinueToPrint(e, item.storyId, item.storyTitle, item.orderId);
                        }}
                        className="flex w-full items-center justify-center rounded-md border border-transparent bg-indigo-600 px-2.5 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:w-full sm:flex-grow-0"
                      >
                        View order progress
                      </button>
                    </div>
                  </div>
                </div>
              </section>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageNav className="absolute inset-x-0 top-0 z-50" />
      {printOrders.length > 0 ? (
        <PrintOrdersDisplay />
      ) : (
        <div className="bg-white">
          <div className="mx-auto max-w-3xl px-4 py-16 sm:px-6 sm:py-24">
            <div className="max-w-xl">
              <h1
                id="print-orders-heading"
                className="text-3xl font-bold tracking-tight text-gray-900"
              >
                Your Print Books
              </h1>
              <p className="mt-2 text-sm text-gray-500">
              No print book orders found. Go to your <a href="/library" className="text-indigo-600 hover:text-indigo-500 font-medium">Library</a>, select a story you've created, and purchase a print book to see your orders here!              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}